import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ApiError, errorCode, errorMessage } from '../../api/client'
import { useRequestCode, useVerifyCode } from '../../api/queries'
import { Button, Field, Input } from '../../components/ui'
import { ALLOWED_DOMAIN, isAllowedEmail } from '../../lib/config'
import { fmtDuration } from '../../lib/time'
import { AuthShell } from './AuthShell'

/**
 * Экраны 1.1 и 1.2 — почта и код.
 *
 * Отдельной регистрации нет: первый вход и все последующие выглядят одинаково.
 * Кнопка «Получить код» неактивна, пока адрес не из домена компании, — человек
 * узнаёт об ошибке до отправки формы, а не после письма, которое не придёт.
 */
export function LoginPage() {
  const [step, setStep] = useState<'email' | 'code'>('email')
  const [email, setEmail] = useState(() => prefilledEmail())
  const [expiresAt, setExpiresAt] = useState<string | null>(null)
  /** Момент, когда кнопку повторной отправки снова можно нажать. */
  const [resendAt, setResendAt] = useState<number | null>(null)

  const navigate = useNavigate()
  const location = useLocation()
  const requestCode = useRequestCode()
  const verify = useVerifyCode()

  const from = (location.state as { from?: string } | null)?.from ?? '/'

  function send(target: string) {
    // Прошлый отказ по коду относился к прошлому письму: после нового кода
    // «код больше не действует» на экране — это ложь и ещё один тупик.
    verify.reset()
    requestCode.mutate(target, {
      onSuccess: (data) => {
        setExpiresAt(data.expires_at)
        setResendAt(Date.now() + data.resend_after_seconds * 1000)
        setStep('code')
      },
      onError: (error) => {
        // Пауза между письмами — не отказ во входе: первое письмо уже пришло и код
        // в нём действует. Оставлять человека на экране почты в этот момент значило
        // отправить его в тупик: уйти некуда, а поля для кода на экране нет.
        // Поэтому 429 ведёт туда же, куда и успех, — на экран ввода кода.
        if (errorCode(error) === 'rate_limited') {
          setResendAt(Date.now() + retryAfterMs(error))
          setStep('code')
        }
      },
    })
  }

  if (step === 'code') {
    return (
      <CodeStep
        email={email}
        expiresAt={expiresAt}
        resendAt={resendAt}
        onResend={() => send(email)}
        resending={requestCode.isPending}
        resendError={requestCode.error}
        verifyError={verify.error}
        pending={verify.isPending}
        onBack={() => {
          verify.reset()
          requestCode.reset()
          setStep('email')
        }}
        onSubmit={(code) => {
          verify.mutate(
            { email, code },
            {
              onSuccess: (data) => {
                if (data.user.status === 'deactivated') {
                  navigate('/blocked', { replace: true })
                  return
                }
                navigate(data.needs_profile ? '/welcome' : from, { replace: true })
              },
            },
          )
        }}
      />
    )
  }

  const valid = isAllowedEmail(email)

  return (
    <AuthShell
      title="Вход в «Переговорки»"
      subtitle="Введите рабочую почту — пришлём код. Пароль не нужен."
    >
      <form
        onSubmit={(event) => {
          event.preventDefault()
          if (valid) send(email.trim().toLowerCase())
        }}
        className="flex flex-col gap-4"
      >
        <Field
          label="Рабочая почта"
          hint={
            email.length > 0 && !valid
              ? undefined
              : `Только адреса в домене ${ALLOWED_DOMAIN}`
          }
          error={
            email.length > 0 && !valid
              ? `Нужен рабочий адрес в домене ${ALLOWED_DOMAIN}. Личные почты система не пускает.`
              : undefined
          }
        >
          <Input
            type="email"
            inputMode="email"
            autoComplete="email"
            autoFocus
            className="tnum"
            placeholder={`i.ivanov@${ALLOWED_DOMAIN}`}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>

        {requestCode.error && <AuthError error={requestCode.error} />}

        <Button type="submit" variant="primary" size="lg" disabled={!valid} loading={requestCode.isPending}>
          Получить код
        </Button>
      </form>
    </AuthShell>
  )
}

/**
 * Сколько держать кнопку повторной отправки, когда сервер ответил «слишком часто».
 *
 * Совпадает с resendCooldown в internal/auth и нужен только на случай ответа без
 * подробностей — например, от промежуточного узла. Округление в большую сторону
 * намеренное: лишние секунды ожидания безобидны, а кнопка, ожившая раньше времени,
 * приносит вторую такую же ошибку.
 */
const RESEND_FALLBACK_MS = 60_000

/**
 * Остаток паузы из ответа сервера.
 *
 * Число сервер присылает отдельным полем (details.retry_after_seconds) — ровно
 * затем, чтобы отсчёт у кнопки совпадал с настоящим остатком, а не держал полную
 * минуту там, где ждать девятнадцать секунд. Из текста сообщения это же число
 * не выковыривается принципиально: правка формулировки молча ломала бы отсчёт.
 */
function retryAfterMs(error: unknown): number {
  const raw = error instanceof ApiError ? error.details?.retry_after_seconds : undefined
  const seconds = Number(raw)
  if (!Number.isFinite(seconds) || seconds <= 0) return RESEND_FALLBACK_MS
  return seconds * 1000
}

/**
 * Остаток паузы словами.
 *
 * Секундами меряется только пауза между письмами. Часовой предел на тот же адрес
 * отпускает через 54 минуты, и «через 3240 с» — это число, которое человек считает
 * в уме, чтобы понять, идти ему пить чай или писать администратору.
 */
function fmtLeft(seconds: number): string {
  return seconds > 90 ? fmtDuration(Math.ceil(seconds / 60)) : `${seconds} с`
}

/**
 * Сообщение сервера о паузе без его собственного числа.
 *
 * Число сервер посчитал в момент отказа, и оно застыло: рядом стояли живой отсчёт
 * «Отправить заново через 1 с» и неподвижное «Запросить новый код можно через 42 с».
 * Два числа об одном и том же, оба от системы, и одно из них врёт. Живым остаётся
 * отсчёт у кнопки; фраза с застывшим числом убирается, остальное объяснение —
 * «письмо уже отправлено», «проверьте „Спам“», «напишите администратору» —
 * остаётся словами сервера.
 *
 * Точка считается концом предложения, только если за ней пробел или конец строки:
 * иначе «22.08.2026» в других сообщениях распалось бы на куски.
 */
function withoutFrozenCountdown(message: string): string {
  const pieces = message.split(/([.!?])(?:\s+|$)/).filter((piece) => piece !== '')
  const sentences: string[] = []
  for (let index = 0; index < pieces.length; index += 2) {
    sentences.push((pieces[index] + (pieces[index + 1] ?? '')).trim())
  }
  const kept = sentences.filter((sentence) => sentence && !/через \d/.test(sentence)).join(' ')
  return kept || message
}

/** Экран 1.2. Кнопки «Войти» нет: форму отправляет ввод последней цифры. */
function CodeStep({
  email,
  expiresAt,
  resendAt,
  onResend,
  resending,
  onSubmit,
  onBack,
  resendError,
  verifyError,
  pending,
}: {
  email: string
  expiresAt: string | null
  resendAt: number | null
  onResend: () => void
  resending: boolean
  onSubmit: (code: string) => void
  onBack: () => void
  /** Отказ в повторной отправке. Показывается у кнопки, поле с кодом не трогает. */
  resendError: unknown
  /** Отказ по самому коду: ошибся цифрой, код истёк, попытки кончились. */
  verifyError: unknown
  pending: boolean
}) {
  const [code, setCode] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const submitted = useRef('')

  const codeExpired = useCountdownToExpiry(expiresAt)
  const resendIn = useSecondsUntil(resendAt)
  const paused = resendIn > 0

  // Пауза между письмами — состояние экрана, а не отказ во входе, и число в ней
  // одно: живой отсчёт у кнопки. Сообщение сервера остаётся объяснением («письмо
  // уже отправлено, проверьте „Спам“»), но без своей фразы об остатке — она
  // посчитана в момент отказа и через секунду уже неправда. И вместе с паузой
  // сообщение гаснет: «подождите 47 с» через минуту после того, как ждать нечего, —
  // это уже не объяснение, а мусор на экране.
  //
  // И только вместо отказа по коду, а не вместе с ним. Плашка о паузе висит минуту;
  // за эту минуту человек успевает ввести код и ошибиться цифрой — и тогда на карточке
  // оказывались два сообщения сразу: сверху «Код неверный», снизу «Письмо уже
  // отправлено». Карточка вырастала на две строки и, поскольку она центрирована
  // по вертикали, прыгала под курсором. Второе сообщение при этом отвечает на вопрос,
  // которого человек больше не задаёт: письмо он уже получил, код из него и вводил.
  const rateLimited = errorCode(resendError) === 'rate_limited'
  const pauseNotice =
    rateLimited && paused && !verifyError ? withoutFrozenCountdown(errorMessage(resendError)) : null
  const resendFailure = rateLimited ? null : resendError

  // Неверный код: поле очищается, чтобы не набирать поверх старого. Отказ в повторной
  // отправке поле не трогает — набранные цифры в нём не виноваты.
  useEffect(() => {
    if (verifyError) {
      setCode('')
      submitted.current = ''
      inputRef.current?.focus()
    }
  }, [verifyError])

  const apiCode = verifyError instanceof ApiError ? verifyError.code : undefined
  const needNewCode = apiCode === 'code_expired' || apiCode === 'too_many_attempts' || codeExpired.expired

  return (
    <AuthShell
      title="Код из письма"
      subtitle={
        <>
          Отправили шесть цифр на <span className="tnum font-medium text-fg">{email}</span>.
          Письмо приходит за несколько секунд; если его нет — проверьте папку «Спам».
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {/* Поле не исчезает ни при одной ошибке: код из уже пришедшего письма
            действует час, и ввести его человек должен в любой момент. */}
        <Field
          id="login-code"
          label="Код"
          hint={
            expiresAt && !codeExpired.expired
              ? `Код действует ещё ${codeExpired.text}`
              : expiresAt
                ? undefined
                : // Сюда попадают после отказа «письмо уже отправлено»: своего кода
                  // этот экран не запрашивал и срока его не знает, но код из пришедшего
                  // письма живой, и человеку надо сказать, что вводить его есть смысл.
                  'Шесть цифр из последнего письма — код живёт час'
          }
        >
          <input
            id="login-code"
            ref={inputRef}
            className="field tnum text-center text-3xl font-semibold tracking-[0.4em]"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            maxLength={6}
            value={code}
            disabled={pending}
            placeholder="······"
            onChange={(event) => {
              const digits = event.target.value.replace(/\D/g, '').slice(0, 6)
              setCode(digits)
              // Отправляем ровно один раз на набранный код: без этого повторный
              // ререндер шлёт второй запрос и съедает попытку.
              if (digits.length === 6 && submitted.current !== digits) {
                submitted.current = digits
                onSubmit(digits)
              }
            }}
          />
        </Field>

        {verifyError ? <AuthError error={verifyError} /> : null}

        {needNewCode && (
          <Button variant="primary" onClick={onResend} loading={resending} disabled={paused}>
            {paused ? `Новый код можно запросить через ${fmtLeft(resendIn)}` : 'Прислать новый код'}
          </Button>
        )}

        <div className="flex items-center justify-between gap-3">
          <button type="button" onClick={onBack} className="text-sm text-muted hover:text-fg">
            Другой адрес
          </button>
          {/* Вторая такая же кнопка рядом с первой только запутывает: когда наверху
              стоит «Прислать новый код», здесь остаётся один выход на экран почты. */}
          {!needNewCode && (
            <button
              type="button"
              disabled={paused || resending}
              onClick={onResend}
              className="text-sm text-accent disabled:text-muted"
            >
              {paused ? `Отправить заново через ${fmtLeft(resendIn)}` : 'Отправить код заново'}
            </button>
          )}
        </div>

        {/* Отказ в повторной отправке — рядом с кнопкой, которую нажали, а не вместо
            экрана: поле с уже набранным кодом трогать не за что. */}
        {pauseNotice ? (
          <p role="status" className="rounded-lg border border-line bg-surface px-3 py-2 text-sm text-muted">
            {pauseNotice}
          </p>
        ) : null}
        {resendFailure ? <AuthError error={resendFailure} /> : null}
      </div>
    </AuthShell>
  )
}

/**
 * Тексты ошибок входа берём с сервера — он объясняет и что произошло, и что делать.
 * Свой текст поверх серверного заводить нельзя: два источника правды разойдутся.
 */
function AuthError({ error }: { error: unknown }) {
  return (
    <p role="alert" className="rounded-lg border border-state-busy/40 bg-state-busy/10 px-3 py-2 text-sm">
      {errorMessage(error)}
    </p>
  )
}

function useCountdownToExpiry(expiresAt: string | null) {
  const now = useTicker()
  return useMemo(() => {
    if (!expiresAt) return { expired: false, text: '' }
    const left = Math.round((new Date(expiresAt).getTime() - now) / 1000)
    if (left <= 0) return { expired: true, text: '' }
    return { expired: false, text: fmtDuration(Math.max(1, Math.round(left / 60))) }
  }, [expiresAt, now])
}

/**
 * Сколько секунд осталось до момента. Отсчёт ведётся от абсолютного времени, а не
 * уменьшением счётчика на единицу: счётчик врёт при любом лишнем ререндере, а
 * уснувшая вкладка останавливает его вовсе — кнопка так и остаётся неактивной.
 */
function useSecondsUntil(deadline: number | null): number {
  const now = useTicker()
  if (deadline === null) return 0
  return Math.max(0, Math.ceil((deadline - now) / 1000))
}

/** Общее сердцебиение раз в секунду для обоих отсчётов на экране. */
function useTicker(): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [])
  return now
}

/**
 * Ссылка из письма-приглашения ведёт на вход с уже подставленным адресом: человек,
 * который в системе ни разу не был, не должен вспоминать, каким адресом его позвали.
 */
function prefilledEmail(): string {
  const value = new URLSearchParams(window.location.search).get('email')
  return value ? value.trim().toLowerCase() : ''
}
